/**
 * The plain words around a lead's next step: why a row is on a Today list and when a step
 * falls due. The API words the step itself (`next_step_label`); nothing here counts touches.
 */
import type { LeadDetail, LeadSummary, TodayListKey } from "@/api/models";
import { daysBetween, prettyDay } from "./dates";

/** "23 Sep", with the year only when it is not the current one. */
export function shortDay(day: string | null | undefined, today: string): string {
  const full = prettyDay(day);
  if (!full || !day) return "";
  return day.slice(0, 4) === today.slice(0, 4) ? full.slice(0, -5) : full;
}

/** "Court approved today", "Court approved yesterday", "Court approved 3 days ago". */
export function approvedAgo(days: number | null | undefined): string {
  if (days === null || days === undefined) return "Court approved";
  if (days <= 0) return "Court approved today";
  if (days === 1) return "Court approved yesterday";
  return `Court approved ${days} days ago`;
}

/** " · found today" or " · found 7 Oct" when we saw the approval after the court's day, else "". */
export function foundLate(lead: Pick<LeadSummary, "approved_on" | "approval_found_on">, today: string): string {
  const found = lead.approval_found_on;
  if (!found || !lead.approved_on || found <= lead.approved_on) return "";
  return found >= today ? " · found today" : ` · found ${shortDay(found, today)}`;
}

/** The short reason a row is on its Today list, or "" when the list title says it all. */
export function rowReason(list: TodayListKey, lead: LeadSummary, today: string): string {
  switch (list) {
    case "executor_just_approved":
      return approvedAgo(lead.days_since_approval) + foundLate(lead, today);
    case "follow_ups_due":
      return "They asked you to call back";
    case "drop":
      return lead.drop_reason ?? "";
    case "letters_to_send":
      return `${lead.filing_date_label} ${shortDay(lead.filing_date, today)}`;
    case "calls_to_make":
      return lead.next_touch_due && lead.next_touch_due < today ? `Waiting since ${shortDay(lead.next_touch_due, today)}` : "";
  }
}

/** A lead the agent should stop contacting: the house was listed or sold, or the family said no. */
export function shouldStop(lead: Pick<LeadSummary, "next_step" | "dropped" | "drop_reason">): boolean {
  return lead.next_step === "done" && !lead.dropped && Boolean(lead.drop_reason);
}

/** When a step that is not due yet comes up, for the Leads list: "Next on 1 Oct", or "". */
export function nextStepWhen(lead: LeadSummary, today: string): string {
  if (lead.next_step !== "none" || !lead.next_touch_due) return "";
  return `Next on ${shortDay(lead.next_touch_due, today)}`;
}

/** One sentence for a toast after a change: "Next: call, from 2 Oct." */
export function nextStepSentence(lead: LeadDetail): string {
  if (lead.next_step === "done") return lead.drop.suggested_reason ? "Next: stop contacting them." : "Nothing more to do with this lead.";
  if (lead.next_step === "none") {
    const next = lead.track.next_touch;
    return next ? `Nothing due until ${shortDay(next.due_date, lead.date)}.` : "Nothing due.";
  }
  return `Next: ${lead.next_step_label.charAt(0).toLowerCase()}${lead.next_step_label.slice(1)}.`;
}

/** "around 10 Oct", "any day now" once that day has passed, or null where the county's court has not been timed. */
function expectedWords(expected: string | null, today: string): string | null {
  if (!expected) return null;
  return expected < today ? "any day now" : `around ${shortDay(expected, today)}`;
}

/**
 * One sentence under the timeline: why the next step is what it is. The method: a letter
 * when the case is filed, a call a week later, a call as soon as the court approves
 * approving the executor, then once a month.
 */
export function whySentence(lead: LeadDetail): string {
  if (lead.next_step === "none" && lead.track.next_touch?.kind === "letter" && !lead.petitioner.mailing_address.value) {
    return "A mailing address is needed before you can send a letter.";
  }
  const { timeline } = lead;
  const today = lead.date;
  const expected = expectedWords(timeline.approval_expected_by, today);
  const days = timeline.approved_on ? daysBetween(timeline.approved_on, today) : null;
  const approved = `The court approved the executor ${days === null ? "" : days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`}`.trim();
  if (lead.next_step === "done") {
    return lead.drop.suggested_reason
      ? `${lead.drop.suggested_reason}, so stop contacting them.`
      : "The follow-up with this family is over.";
  }
  switch (timeline.phase) {
    case "approved":
      if (lead.next_step === "call" || lead.next_step === "call_back") {
        return `${approved}: call as soon as you can.`;
      }
      if (lead.next_step === "send_letter") return `${approved}; a letter now, then a call, reaches the family while they decide.`;
      return `${approved}; you have been in touch, so the next call or letter waits its turn.`;
    case "heirs_sign":
      return "No executor is needed here: the heirs can sign now, so reach them this week.";
    case "closed":
      return "The case is closed, so the family has already settled the estate.";
    case "waiting": {
      // No expectation is stated where the county's court has not been timed.
      const by = expected ? `, expected ${expected}` : "";
      if (lead.next_step === "send_letter") {
        return `The court has not approved the executor yet${by}; a letter now means they know you on that day.`;
      }
      if (lead.next_step === "call_back") {
        return expected
          ? `They asked for this call; the court's approval is expected ${expected}, and we will tell you the day it comes.`
          : "They asked for this call; we will tell you the day the court approves the executor.";
      }
      if (lead.next_step === "call") return `A friendly call a week after your letter; the call that counts comes when the court approves${by}.`;
      return `Nothing is due yet; we watch the court every day and will tell you the day it approves the executor${by}.`;
    }
  }
}
